import {
  DetailedHTMLProps, FC, FormEvent, InputHTMLAttributes,
  ReactNode,
} from 'react';
import useTimeInput from 'src/common/hooks/useTimeInput';
import { Field } from 'react-final-form';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import getClassNames from 'src/common/utils/getClassNames';
import InputError from 'src/admin/common/components/InputError';
import { FieldValidator } from 'final-form';
import Label, { Size } from '../Label';
import inputStyles from './Input.module.scss';
import timeInputStyles from './TimeInput.module.scss';
import LoadingSpinner from '../LoadingSpinner';

const styles = { ...timeInputStyles, ...inputStyles };

type Props = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
  required?: boolean;
  label: string;
  name: string;
  onFocus?: () => void;
  onBlur?: (value: any) => void;
  onChange?: (value: any) => void;
  onInvalid?: (event: FormEvent<HTMLInputElement>) => void;
  validate?: FieldValidator<string>;
  fieldKey?: string;
  /**
   * invalidMessage shows error message on submit when time input
   * is in invalid state.
   */
  invalidMessage?: string;
  value?: string;
  hideTimeDropdown?: boolean;
  fullWidth?: boolean;
  optionalLabel?: ReactNode;
  labelSize?: Size;
  isLoading?: boolean;
  preventLayoutShift?: boolean;
  showErrorMessageIcon?: boolean;
  children?: ReactNode;
};

const TimeInput: FC<Props> = ({
  required = false,
  label,
  name,
  onFocus,
  onBlur,
  onChange,
  onInvalid,
  validate,
  value,
  optionalLabel,
  hideTimeDropdown = false,
  fullWidth = false,
  labelSize = 'l',
  invalidMessage = '',
  isLoading = false,
  preventLayoutShift,
  showErrorMessageIcon,
  children,
  ...props
}) => {
  const [t] = useTranslation('common');
  const { timeInputIsSupported } = useTimeInput();
  const clx = [
    styles.input,
    getClassNames(styles, 'timeInput', classNames({ hideTimeDropdown, fullWidth, inputError: invalidMessage.length > 0 })),
  ];

  return (
    <div className={styles.root}>
      <Field
        name={name}
        validate={validate}
      >
        {({ input }) => (
          <Label
            required={required}
            label={label}
            inlineSlot={optionalLabel}
            htmlFor={name}
            size={labelSize}
          >
            <div className={styles.inputField}>
              <input
                type="time"
                id={name}
                title={timeInputIsSupported === false ? t('time-input--title-suggestion') : ''}
                maxLength={8} // is for fallback
                size={8} // is for fallback (field size in characters)
                className={clx.join(' ')}
                {...props}
                {...input}
                {...(value !== undefined ? { value } : {})}
                onFocus={(() => {
                  if (onFocus) onFocus();
                  input.onFocus();
                })}
                onBlur={((event) => {
                  if (onBlur) onBlur(event);
                  input.onBlur(event);
                })}
                onChange={((event) => {
                  if (onChange) onChange(event);
                  input.onChange(event);
                })}
                onInvalid={onInvalid}
              />
              {isLoading && (
                <div className={styles.loader}>
                  <LoadingSpinner variant="s" />
                </div>
              )}
              {children}
            </div>
            {!!invalidMessage && (
              <InputError
                preventLayoutShift={preventLayoutShift}
                showErrorMessageIcon={showErrorMessageIcon}
              >
                {invalidMessage}
              </InputError>
            )}
          </Label>
        )}
      </Field>
    </div>
  );
};

export default TimeInput;
